import { SkillDetailErrorBoundary } from "@domains/assistant/client/components/SkillDetailErrorBoundary.tsx";
import { initAssistantI18next } from "@domains/assistant/client/i18n/i18next.ts";
import { REVIEWEE_FILTER_QUERY_KEYS } from "@domains/reviews/client/components/reviewee-filters/useRevieweeFilters.ts";
import { initReviewCyclesI18next } from "@domains/reviews/client/i18n/i18next.ts";
import { initSegmentsI18next } from "@domains/segments/client/i18n/i18next.ts";
import { AdjustmentsHorizontalIcon, BoltIcon, BuildingOffice2Icon, ClipboardDocumentListIcon, CreditCardIcon, NewspaperIcon, PuzzlePieceIcon, ShieldCheckIcon, Squares2X2Icon, UserGroupIcon, UsersIcon } from "@heroicons/react/24/outline";
import { hasAnyRole, type Role } from "@packages/core/session/roles.ts";
import { ADMIN_ONLY_ACCESS, ORGANIZATION_PEOPLE_ACCESS, PERFORMANCE_CYCLE_DETAILS_ACCESS, PERFORMANCE_CYCLE_RESULTS_ACCESS, PERFORMANCE_CYCLES_ACCESS } from "@packages/core/access/settingsAccess.ts";
import type { AppDeferredRegistrationData, AppModuleRegisterFunction } from "@packages/core-client/registration/appModuleRegisterFunction.ts";
import { registerArea, type NavGroupBuilder } from "@packages/core-client/registration/registerArea.tsx";
import type { FireflyRuntime } from "@squide/firefly";
import type { i18n } from "i18next";

import { initI18next } from "./i18n/i18next.ts";
import { ComingSoonPage } from "./pages/ComingSoonPage.tsx";
import { FEATURE_ACCESS_SEGMENT, FeatureSegments } from "./pages/features/featurePaths.ts";
import { PersonDetailErrorBoundary } from "./pages/manage-people/detail/PersonDetailErrorBoundary.tsx";
import { CYCLE_DETAIL_ORIGINS, cycleBuilderAreaSegment, cycleCreateAreaSegment, cycleDetailAreaSegment, type CycleDetailOrigin } from "./pages/review-cycles/cycleDetailPaths.ts";
import { ReviewCycleDetailHeading } from "./pages/review-cycles/ReviewCycleDetailHeading.tsx";
import { SKILL_LIBRARY_PATH, SKILL_LIBRARY_SEGMENT } from "./pages/skill-library/skillLibraryPaths.ts";

import { PersonDetailTabsHeading } from "./pages/manage-people/detail/PersonDetailTabsHeading.tsx";
import { useRenderPersonDetailAsTitleOnly } from "./pages/manage-people/detail/useRenderPersonDetailAsTitleOnly.ts";
import { SegmentFormErrorBoundary } from "./pages/segments/form/SegmentFormErrorBoundary.tsx";
import { useIsSharedWithMeSegmentsTabHidden } from "./pages/segments/list/useIsSharedWithMeSegmentsTabHidden.ts";
import { useSegmentTabCounts } from "./pages/segments/list/useSegmentTabCounts.ts";
import { SegmentsHeading } from "./pages/segments/SegmentsHeading.tsx";

const comingSoon = async () => ({ element: <ComingSoonPage /> });

async function loadFeatureAccessPage() {
    const { FeatureAccessPage } = await import("./pages/features/access/FeatureAccessPage.tsx");

    return { element: <FeatureAccessPage /> };
}

async function loadPersonDetailsTab() {
    const { PersonDetailsPage } = await import("./pages/manage-people/detail/PersonDetailsPage.tsx");

    return { element: <PersonDetailsPage /> };
}

async function loadPersonDetailJobTab() {
    const { PersonDetailJobPage } = await import("./pages/manage-people/detail/PersonDetailJobPage.tsx");

    return { element: <PersonDetailJobPage /> };
}

async function loadPersonDetailPermissionsTab() {
    const { PersonDetailPermissionsPage } = await import("./pages/manage-people/detail/PersonDetailPermissionsPage.tsx");

    return { element: <PersonDetailPermissionsPage /> };
}

function registerManagePeopleDetailRoutes(people: NavGroupBuilder) {
    const personTabs = people.registerTabsArea("manage-people-detail", "/people/:personId", {
        access: ORGANIZATION_PEOPLE_ACCESS,
        Heading: PersonDetailTabsHeading,
        hideTitle: true,
        errorElement: <PersonDetailErrorBoundary />,
        commandMenu: false,
        useRenderAsTitleOnly: useRenderPersonDetailAsTitleOnly
    });

    const details = personTabs.registerRoute("details", {
        index: true,
        page: "AdminManagePersonDetails",
        lazy: loadPersonDetailsTab
    });
    const job = personTabs.registerRoute("job", {
        path: "job",
        page: "AdminManagePersonJob",
        lazy: loadPersonDetailJobTab
    });
    const permissions = personTabs.registerRoute("permissions", {
        path: "permissions",
        page: "AdminManagePersonPermissions",
        lazy: loadPersonDetailPermissionsTab
    });

    details.registerLink({ priority: 1000 });
    job.registerLink({ priority: 999 });
    permissions.registerLink({ priority: 998 });
}

async function registerMsw(runtime: FireflyRuntime) {
    if (runtime.isMswEnabled) {
        const { getRequestHandlers } = await import("./api/mocks/getRequestHandlers.ts");

        runtime.registerRequestHandlers(getRequestHandlers());
    }
}

function registerJobRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const jobArea = registerArea(runtime, "admin-settings-job", "/settings/jobs/:jobId", UsersIcon, i18nextInstance, { access: ORGANIZATION_PEOPLE_ACCESS });

    jobArea.registerPage({
        page: "AdminJobDetail",
        lazy: async () => {
            const { JobPage } = await import("./pages/jobs/job/JobPage.tsx");
            const { JobErrorBoundary } = await import("./pages/jobs/job/JobErrorBoundary.tsx");

            return { element: <JobPage />, errorElement: <JobErrorBoundary /> };
        }
    });
}

function registerReviewCycleDetailRoutes(runtime: FireflyRuntime, i18nextInstance: i18n, origin: CycleDetailOrigin) {
    const detailAccess = origin === "home" ? PERFORMANCE_CYCLE_DETAILS_ACCESS : PERFORMANCE_CYCLES_ACCESS;
    const cycleDetailArea = registerArea(runtime, `review-cycle-detail-${origin}`, cycleDetailAreaSegment(origin), ClipboardDocumentListIcon, i18nextInstance, { access: detailAccess });

    const cycleTabs = cycleDetailArea.registerTabsArea("main", {
        Heading: () => <ReviewCycleDetailHeading origin={origin} />,
        hideTitle: true,
        commandMenu: false,
        preserveSearchParams: REVIEWEE_FILTER_QUERY_KEYS
    });

    const progression = cycleTabs.registerRoute("progression", {
        index: true,
        page: "ReviewCycleProgression",
        lazy: async () => {
            const { ReviewCycleProgressionPage } = await import("./pages/review-cycles/ReviewCycleProgressionPage.tsx");

            return { element: <ReviewCycleProgressionPage /> };
        }
    });

    const resultsAccess = { access: PERFORMANCE_CYCLE_RESULTS_ACCESS };

    const scores = cycleTabs.registerRoute("scores", {
        path: "scores",
        page: "ReviewCycleScores",
        ...resultsAccess,
        lazy: async () => {
            const { ReviewCycleScoresPage } = await import("./pages/review-cycles/ReviewCycleScoresPage.tsx");

            return { element: <ReviewCycleScoresPage /> };
        }
    });
    const calibration = cycleTabs.registerRoute("calibration", {
        path: "calibration",
        page: "ReviewCycleCalibration",
        ...resultsAccess,
        lazy: async () => {
            const { ReviewCycleCalibrationPage } = await import("./pages/review-cycles/ReviewCycleCalibrationPage.tsx");

            return { element: <ReviewCycleCalibrationPage /> };
        }
    });
    const questions = cycleTabs.registerRoute("questions", {
        path: "questions",
        page: "ReviewCycleQuestionResults",
        ...resultsAccess,
        lazy: async () => {
            const { ReviewCycleQuestionResultsPage } = await import("./pages/review-cycles/ReviewCycleQuestionResultsPage.tsx");

            return { element: <ReviewCycleQuestionResultsPage /> };
        }
    });
    const themes = cycleTabs.registerRoute("themes", {
        path: "themes",
        page: "ReviewCycleThemes",
        ...resultsAccess,
        lazy: async () => {
            const { ReviewCycleThemesPage } = await import("./pages/review-cycles/ReviewCycleThemesPage.tsx");

            return { element: <ReviewCycleThemesPage /> };
        }
    });

    progression.registerLink({ priority: 1000 });

    return { scores, calibration, questions, themes };
}

function registerReviewCycleBuilderRoutes(runtime: FireflyRuntime, i18nextInstance: i18n, origin: CycleDetailOrigin) {
    const builderArea = registerArea(runtime, `review-cycle-builder-${origin}`, cycleBuilderAreaSegment(origin), ClipboardDocumentListIcon, i18nextInstance, { access: PERFORMANCE_CYCLES_ACCESS });

    builderArea.registerPage({
        page: "ReviewCycleBuilder",
        fullHeight: true,
        lazy: async () => {
            const { ReviewCycleBuilderPage } = await import("./pages/review-cycles/ReviewCycleBuilderPage.tsx");

            return { element: <ReviewCycleBuilderPage origin={origin} /> };
        }
    });

    const createArea = registerArea(runtime, `review-cycle-new-${origin}`, cycleCreateAreaSegment(origin), ClipboardDocumentListIcon, i18nextInstance, { access: PERFORMANCE_CYCLES_ACCESS });

    createArea.registerPage({
        page: "ReviewCycleCreate",
        lazy: async () => {
            const { NewReviewCyclePage } = await import("./pages/review-cycles/NewReviewCyclePage.tsx");

            return { element: <NewReviewCyclePage origin={origin} /> };
        }
    });
}

function registerJobArchitectureAiSetupRoute(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const jobArchitectureAiSetupArea = registerArea(runtime, "job-architecture-ai-setup", "/settings/jobs/get-started/ai", UserGroupIcon, i18nextInstance, { access: ORGANIZATION_PEOPLE_ACCESS });

    jobArchitectureAiSetupArea.registerPage({
        page: "AdminJobsArchitectureAi",
        fullHeight: true,
        lazy: async () => {
            const { JobArchitectureAiSetupPage } = await import("./pages/jobs/ai-setup/JobArchitectureAiSetupPage.tsx");

            return { element: <JobArchitectureAiSetupPage /> };
        }
    });
}

export const registerAdminSettings: AppModuleRegisterFunction = async runtime => {
    const i18nextInstance = initI18next(runtime);
    initAssistantI18next(runtime);
    initSegmentsI18next(runtime);
    initReviewCyclesI18next(runtime);

    await registerMsw(runtime);

    const cycleResultsTabs = CYCLE_DETAIL_ORIGINS.map(origin => {
        registerReviewCycleBuilderRoutes(runtime, i18nextInstance, origin);

        return registerReviewCycleDetailRoutes(runtime, i18nextInstance, origin);
    });

    registerJobArchitectureAiSetupRoute(runtime, i18nextInstance);
    registerJobRoutes(runtime, i18nextInstance);

    const area = registerArea(runtime, "admin-settings", "/settings", BuildingOffice2Icon, i18nextInstance);

    const organization = area.registerSidebarArea("organization");

    const company = organization.registerTabsArea("company", "/company-info", {
        landing: true,
        access: ADMIN_ONLY_ACCESS
    });

    const companyProfile = company.registerRoute("profile", {
        index: true,
        page: "AdminOrganizationCompanyProfile",
        lazy: async () => {
            const { CompanyProfilePage } = await import("./pages/company/CompanyProfilePage.tsx");

            return { element: <CompanyProfilePage /> };
        }
    });

    const companyMissionAndValues = company.registerRoute("mission-and-values", {
        path: "/mission-and-values",
        page: "AdminOrganizationCompanyMissionAndValues",
        lazy: async () => {
            const { CompanyMissionAndValuesPage } = await import("./pages/company/CompanyMissionAndValuesPage.tsx");

            return { element: <CompanyMissionAndValuesPage /> };
        }
    });

    companyProfile.registerLink({ priority: 1000 });
    companyMissionAndValues.registerLink({ priority: 999 });

    const featuresList = organization.registerRoute("features", {
        path: "/features",
        page: "AdminFeatures",
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: async () => {
            const { FeaturesPage } = await import("./pages/features/list/FeaturesPage.tsx");

            return { element: <FeaturesPage /> };
        }
    });

    const people = organization.registerNavGroup("people", { path: "/people" });

    const managePeople = people.registerRoute("manage-people", {
        path: "/people",
        page: "AdminManagePeople",
        landing: true,
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: async () => {
            const { PeoplePage } = await import("./pages/PeoplePage.tsx");

            return { element: <PeoplePage /> };
        }
    });

    const employeeFields = people.registerRoute("people-fields", {
        path: "/people-fields",
        page: "AdminPeopleFields",
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: async () => {
            const { PeopleFieldsPage } = await import("./pages/PeopleFieldsPage.tsx");

            return { element: <PeopleFieldsPage /> };
        }
    });

    registerManagePeopleDetailRoutes(people);

    const jobs = people.registerRoute("jobs", {
        path: "/jobs",
        page: "AdminJobs",
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: async () => {
            const { JobsPage } = await import("./pages/jobs/JobsPage.tsx");

            return { element: <JobsPage /> };
        }
    });

    people.registerRoute("job-new", {
        path: "/jobs/new",
        page: "AdminJobCreate",
        noHeading: true,
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: async () => {
            const { NewJobPage } = await import("./pages/jobs/NewJobPage.tsx");

            return { element: <NewJobPage /> };
        }
    });

    people.registerRoute("jobs-get-started", {
        path: "/jobs/get-started",
        page: "AdminJobsGetStarted",
        noHeading: true,
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: async () => {
            const { JobArchitectureGetStartedPage } = await import("./pages/jobs/JobArchitectureGetStartedPage.tsx");

            return { element: <JobArchitectureGetStartedPage /> };
        }
    });

    people.registerRoute("job-architecture", {
        path: "/jobs/architecture",
        page: "AdminJobArchitecture",
        access: ORGANIZATION_PEOPLE_ACCESS,
        noHeading: true,
        lazy: async () => {
            const { JobArchitecturePage } = await import("./pages/jobs/JobArchitecturePage.tsx");

            return { element: <JobArchitecturePage /> };
        }
    });

    const segments = people.registerTabsArea("segments", "/segments", {
        access: ORGANIZATION_PEOPLE_ACCESS,
        preserveSearchParams: ["query"],
        Heading: SegmentsHeading,
        useTabBadgeCount: useSegmentTabCounts,
        useIsTabHidden: useIsSharedWithMeSegmentsTabHidden
    });

    const mySegments = segments.registerRoute("mine", {
        index: true,
        page: "AdminSegmentsMine",
        lazy: async () => {
            const { MySegmentsPage } = await import("./pages/segments/list/MySegmentsPage.tsx");

            return { element: <MySegmentsPage /> };
        }
    });

    const sharedWithMeSegments = segments.registerRoute("shared-with-me", {
        path: "/shared-with-me",
        page: "AdminSegmentsSharedWithMe",
        lazy: async () => {
            const { SharedWithMeSegmentsPage } = await import("./pages/segments/list/SharedWithMeSegmentsPage.tsx");

            return { element: <SharedWithMeSegmentsPage /> };
        }
    });

    mySegments.registerLink({ priority: 1000 });
    sharedWithMeSegments.registerLink({ priority: 999 });

    const newSegment = people.registerTabsArea("segment-new", "/segments/new", {
        access: ORGANIZATION_PEOPLE_ACCESS,
        useRenderAsTitleOnly: () => true,
        commandMenu: false
    });

    newSegment.registerRoute("view", {
        index: true,
        page: "AdminSegmentCreate",
        lazy: async () => {
            const { NewSegmentPage } = await import("./pages/segments/form/NewSegmentPage.tsx");

            return { element: <NewSegmentPage /> };
        }
    });

    const editSegment = people.registerTabsArea("segment-edit", "/segments/:segmentId", {
        access: ORGANIZATION_PEOPLE_ACCESS,
        useRenderAsTitleOnly: () => true,
        commandMenu: false,
        errorElement: <SegmentFormErrorBoundary />
    });

    editSegment.registerRoute("view", {
        index: true,
        page: "AdminSegmentEdit",
        lazy: async () => {
            const { EditSegmentPage } = await import("./pages/segments/form/EditSegmentPage.tsx");

            return { element: <EditSegmentPage /> };
        }
    });

    const rolesAndPermissions = organization.registerRoute("roles-and-permissions", {
        path: "/permissions",
        page: "AdminRolesAndPermissions",
        access: ADMIN_ONLY_ACCESS,
        lazy: async () => {
            const { RolesAndPermissionsPage } = await import("./pages/RolesAndPermissionsPage.tsx");

            return { element: <RolesAndPermissionsPage /> };
        }
    });

    const integrations = organization.registerRoute("integrations", {
        path: "/integrations",
        page: "AdminIntegrations",
        access: ADMIN_ONLY_ACCESS,
        lazy: async () => {
            const { IntegrationsPage } = await import("./pages/IntegrationsPage.tsx");

            return { element: <IntegrationsPage /> };
        }
    });

    const billing = organization.registerRoute("billing", {
        path: "/billing",
        page: "AdminBilling",
        access: ADMIN_ONLY_ACCESS,
        lazy: async () => {
            const { BillingPage } = await import("./pages/BillingPage.tsx");

            return { element: <BillingPage /> };
        }
    });

    const agent = area.registerSidebarArea("agent");

    const general = agent.registerRoute("general", {
        path: "/agent",
        page: "AdminAgentGeneral",
        access: ADMIN_ONLY_ACCESS,
        lazy: comingSoon
    });
    const skillLibrary = agent.registerRoute("skill-library", {
        path: SKILL_LIBRARY_SEGMENT,
        page: "AdminAgentSkillLibrary",
        access: ADMIN_ONLY_ACCESS,
        lazy: async () => {
            const { SkillLibraryPage } = await import("./pages/skill-library/list/SkillLibraryPage.tsx");

            return { element: <SkillLibraryPage /> };
        }
    });
    const skillDetail = agent.registerTabsArea("skill-detail", `${SKILL_LIBRARY_SEGMENT}/:skillSlug`, {
        access: ADMIN_ONLY_ACCESS,
        useRenderAsTitleOnly: () => true,
        commandMenu: false,
        errorElement: <SkillDetailErrorBoundary backHref={SKILL_LIBRARY_PATH} />
    });

    skillDetail.registerRoute("view", {
        index: true,
        page: "AdminFeatureSkillDetail",
        lazy: async () => {
            const { SkillDetailPage } = await import("./pages/skill-library/detail/SkillDetailPage.tsx");

            return { element: <SkillDetailPage /> };
        }
    });

    const connectedApps = agent.registerTabsArea("connected-apps", "/agent/connected-apps", {
        access: ADMIN_ONLY_ACCESS
    });

    const connectedAppsKnowledgeSources = connectedApps.registerRoute("knowledge-sources", {
        index: true,
        page: "AdminAgentConnectedAppsKnowledgeSources",
        lazy: async () => {
            const { AdminConnectedAppsKnowledgeSourcesPage } = await import("./pages/connected-apps/AdminConnectedAppsKnowledgeSourcesPage.tsx");

            return { element: <AdminConnectedAppsKnowledgeSourcesPage /> };
        }
    });

    const connectedAppsAccess = connectedApps.registerRoute("access", {
        path: "/access",
        page: "AdminAgentConnectedAppsAccess",
        lazy: async () => {
            const { AdminConnectedAppsAccessPage } = await import("./pages/connected-apps/AdminConnectedAppsAccessPage.tsx");

            return { element: <AdminConnectedAppsAccessPage /> };
        }
    });

    const features = area.registerSidebarArea("features");

    const engagement = features.registerTabsArea("engagement", FeatureSegments.engagement, { access: ORGANIZATION_PEOPLE_ACCESS, commandMenu: false });

    const engagementSettings = engagement.registerRoute("settings", {
        index: true,
        page: "AdminFeatureEngagementSettings",
        lazy: async () => {
            const { EngagementSettingsPage } = await import("./pages/features/engagement/EngagementSettingsPage.tsx");

            return { element: <EngagementSettingsPage /> };
        }
    });

    const engagementAccess = engagement.registerRoute("access", {
        path: FEATURE_ACCESS_SEGMENT,
        page: "AdminFeatureEngagementAccess",
        lazy: loadFeatureAccessPage
    });

    features.registerRoute("goals", {
        path: FeatureSegments.goals,
        page: "AdminFeatureGoals",
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: loadFeatureAccessPage
    });

    const goodVibes = features.registerTabsArea("good-vibes", FeatureSegments["good-vibes"], { access: ORGANIZATION_PEOPLE_ACCESS, commandMenu: false });

    const goodVibesSettings = goodVibes.registerRoute("settings", {
        index: true,
        page: "AdminFeatureGoodVibesSettings",
        lazy: async () => {
            const { GoodVibesSettingsPage } = await import("./pages/features/good-vibes/GoodVibesSettingsPage.tsx");

            return { element: <GoodVibesSettingsPage /> };
        }
    });

    const goodVibesAccess = goodVibes.registerRoute("access", {
        path: FEATURE_ACCESS_SEGMENT,
        page: "AdminFeatureGoodVibesAccess",
        lazy: loadFeatureAccessPage
    });

    features.registerRoute("meetings", {
        path: FeatureSegments.meetings,
        page: "AdminFeatureMeetings",
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: loadFeatureAccessPage
    });

    const performanceReviews = features.registerTabsArea("performance-reviews", FeatureSegments["performance-reviews"], {
        access: [...PERFORMANCE_CYCLES_ACCESS, ...ORGANIZATION_PEOPLE_ACCESS],
        commandMenu: false
    });

    performanceReviews.registerRoute("index", {
        index: true,
        lazy: async () => {
            const { PerformanceReviewsIndexRedirect } = await import("./pages/features/performance-reviews/PerformanceReviewsIndexRedirect.tsx");

            return { element: <PerformanceReviewsIndexRedirect /> };
        }
    });

    const reviewCycles = performanceReviews.registerRoute("review-cycles", {
        path: "/review-cycles",
        page: "AdminFeaturePerformanceReviewCycles",
        landing: true,
        access: PERFORMANCE_CYCLES_ACCESS,
        lazy: async () => {
            const { ManageReviewCyclesPage } = await import("./pages/features/performance-reviews/ManageReviewCyclesPage.tsx");

            return { element: <ManageReviewCyclesPage /> };
        }
    });

    const performanceReviewsAccess = performanceReviews.registerRoute("access", {
        path: FEATURE_ACCESS_SEGMENT,
        page: "AdminFeaturePerformanceReviewsAccess",
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: loadFeatureAccessPage
    });

    features.registerRoute("plans", {
        path: FeatureSegments.plans,
        page: "AdminFeaturePlans",
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: loadFeatureAccessPage
    });

    return (deferredRuntime: FireflyRuntime, { session }: AppDeferredRegistrationData) => {
        const canSee = (roles: readonly Role[]) => hasAnyRole(session, roles);
        const isAdmin = canSee(ADMIN_ONLY_ACCESS);
        const canManageFeatureAccess = deferredRuntime.getFeatureFlag("manage-feature-access", false) && canSee(ORGANIZATION_PEOPLE_ACCESS);

        if (isAdmin) {
            company.registerLink({ priority: 200, icon: BuildingOffice2Icon });
        }

        if (canManageFeatureAccess) {
            featuresList.registerLink({ priority: 195, icon: Squares2X2Icon });

            engagementSettings.registerLink({ priority: 1000 });
            engagementAccess.registerLink({ priority: 999 });
            goodVibesSettings.registerLink({ priority: 1000 });
            goodVibesAccess.registerLink({ priority: 999 });

            if (canSee(PERFORMANCE_CYCLES_ACCESS)) {
                reviewCycles.registerLink({ priority: 1000 });
            }

            performanceReviewsAccess.registerLink({ priority: 999 });
        }

        if (canSee(ORGANIZATION_PEOPLE_ACCESS)) {
            people.registerLink({ priority: 190, icon: UsersIcon });
            managePeople.registerLink({ priority: 30 });
            employeeFields.registerLink({ priority: 20 });
            jobs.registerLink({ priority: 10 });

            segments.registerLink({ priority: 5 });
        }

        if (isAdmin) {
            rolesAndPermissions.registerLink({
                priority: 160,
                icon: ShieldCheckIcon
            });
            integrations.registerLink({ priority: 150, icon: BoltIcon });
            billing.registerLink({ priority: 140, icon: CreditCardIcon });

            general.registerLink({
                priority: 100,
                icon: AdjustmentsHorizontalIcon
            });
            skillLibrary.registerLink({ priority: 90, icon: NewspaperIcon });
            const isConnectedAppsEnabled = deferredRuntime.getFeatureFlag("admin-connected-apps", false);

            if (isConnectedAppsEnabled) {
                connectedApps.registerLink({
                    priority: 80,
                    icon: PuzzlePieceIcon
                });
            }

            connectedAppsKnowledgeSources.registerLink({ priority: 1000, commandMenu: false });
            connectedAppsAccess.registerLink({ priority: 999, commandMenu: isConnectedAppsEnabled });
        }

        if (canSee(PERFORMANCE_CYCLES_ACCESS) && !canManageFeatureAccess) {
            performanceReviews.registerLink({
                priority: 40,
                icon: ClipboardDocumentListIcon
            });
        }

        if (canSee(PERFORMANCE_CYCLE_RESULTS_ACCESS)) {
            for (const { scores, calibration, questions, themes } of cycleResultsTabs) {
                scores.registerLink({ priority: 999 });
                calibration.registerLink({ priority: 998 });
                questions.registerLink({ priority: 997 });
                themes.registerLink({ priority: 996 });
            }
        }
    };
};
